import { QUADRANT_KEYS, computeQuadrantCounts } from "./quadrantUtils";

export const clampPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
};

// Keep labels/colors identical to the existing CustomAnalytics implementation.
export const riskGradeForBottom2 = (pct, riskThresholds) => {
  const p = Number(pct);
  if (!Number.isFinite(p)) return { label: "--", key: "none", color: "#9CA3AF" };

  // Treat thresholds as lower-bounds for each label.
  const t = {
    veryHigh: clampPercent(riskThresholds?.veryHigh),
    high: clampPercent(riskThresholds?.high),
    moderate: clampPercent(riskThresholds?.moderate),
    stable: clampPercent(riskThresholds?.stable),
    low: clampPercent(riskThresholds?.low),
  };

  if (p >= t.veryHigh) return { label: "Very High Risk", key: "very_high", color: "#EF4444" };
  if (p >= t.high) return { label: "High Risk", key: "high", color: "#F97316" };
  if (p >= t.moderate) return { label: "Moderate Risk", key: "moderate", color: "#F59E0B" };
  if (p >= t.stable) return { label: "Generally Stable", key: "stable", color: "#2D70E2" };
  if (p >= t.low) return { label: "Low Risk", key: "low", color: "#10B981" };
  return { label: "Low Risk", key: "low", color: "#10B981" };
};

export const normalizeToPercent = (value, min, max) => {
  if (value == null || isNaN(Number(value))) return null;
  const clamped = Math.max(min, Math.min(max, Number(value)));
  if (max === min) return 0;
  return ((clamped - min) / (max - min)) * 100;
};

// Text normalizer aligned with outcome calculations (to match outcome question text reliably)
export function normalise(str) {
  if (typeof str !== "string") return "";

  let s = str
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/\{.*?}/g, "")
    .toLowerCase();

  s = s.replace(/\b&\b/g, " and ");
  s = s.replace(/[!"#$%()*+,./:;<=>?@[\\\]^_`{|}~]/g, " ");
  s = s.replace(/\s+/g, " ").trim();

  return s;
}

export const average = (vals) => {
  const nums = (vals || [])
    .filter((v) => v != null && !isNaN(Number(v)))
    .map(Number);
  if (!nums.length) return null;
  return nums.reduce((s, v) => s + v, 0) / nums.length;
};

// Build mapping from global question id -> {dimensionIndex, factorIndex, questionIndex, reverse}
export const buildQuestionIdToPath = (dimensions) => {
  const map = new Map();
  const dims = dimensions || [];
  dims.forEach((dimension, dimIdx) => {
    (dimension?.factors || []).forEach((factor, facIdx) => {
      (factor?.questions || []).forEach((q, qIdx) => {
        if (q && q.id != null) {
          map.set(Number(q.id), {
            dimensionIndex: dimIdx,
            factorIndex: facIdx,
            questionIndex: qIdx,
            reverse: !!q.reverse,
          });
        }
      });
    });
  });
  return map;
};

// Helper: get raw question response for a respondent by global id
export const getResponseValueByGlobalId = (respondent, questionGlobalId, scaleCfg, questionIdToPath) => {
  const path = questionIdToPath?.get(Number(questionGlobalId));
  if (!path) return null;
  const items = respondent?.questions || [];
  const match = items.find(
    (q) =>
      Number(q.id) === path.dimensionIndex &&
      Number(q.factor) === path.factorIndex &&
      Number(q.q) === path.questionIndex
  );
  let val = match?.response;
  if (val == null || val === "" || isNaN(Number(val))) return null;
  let num = Number(val);
  if (path.reverse) {
    // Invert on the original scale
    const min = Number(scaleCfg?.min ?? 0);
    const max = Number(scaleCfg?.max ?? 10);
    num = min + max - num;
  }
  return num;
};

export const scoreRespondentForQuestionDim = (respondent, dimDef, questionIdToPath) => {
  const qids = Array.isArray(dimDef?.question_ids) ? dimDef.question_ids : [];
  if (!qids.length) return null;
  const scaleCfg = {
    min: Number(dimDef?.scale?.min ?? 0),
    max: Number(dimDef?.scale?.max ?? 10),
  };
  const values = qids
    .map((qid) => getResponseValueByGlobalId(respondent, qid, scaleCfg, questionIdToPath))
    .filter((v) => v != null);
  return average(values);
};

export const buildOutcomeQuestionSetByOutcomeName = (outcomeQ) => {
  const map = new Map();
  const sections = Array.isArray(outcomeQ?.questions) ? outcomeQ.questions : [];
  sections.forEach((sec) => {
    const secName = sec?.name;
    const secQuestions = Array.isArray(sec?.questions) ? sec.questions : [];
    const set = new Set(
      secQuestions
        .map((q) => q?.question)
        .filter(Boolean)
        .map((q) => normalise(q))
        .filter(Boolean)
    );
    if (secName && set.size) {
      map.set(normalise(String(secName)), set);
    }
  });
  return map;
};

export const scoreRespondentForOutcomeDim = (respondent, dimDef, outcomeQuestionSetByOutcomeName) => {
  const outcomeName = dimDef?.outcome_name;
  if (!outcomeName) return null;

  const set = outcomeQuestionSetByOutcomeName?.get(normalise(String(outcomeName)));
  if (!set || set.size === 0) return null;

  const eo = respondent?.employee_outcomes;
  const responses = Array.isArray(eo?.responses) ? eo.responses : [];
  const vals = responses
    .filter((r) => set.has(normalise(String(r?.question || ""))))
    .map((r) => r?.response)
    .filter((v) => v != null && v !== "" && !isNaN(Number(v)))
    .map(Number);

  return average(vals);
};

export const scoreRespondentForDim = (respondent, dimDef, { questionIdToPath, outcomeQuestionSetByOutcomeName }) => {
  if (!dimDef) return null;
  const type = String(dimDef?.type || "").toLowerCase();
  if (type === "outcome") return scoreRespondentForOutcomeDim(respondent, dimDef, outcomeQuestionSetByOutcomeName);
  return scoreRespondentForQuestionDim(respondent, dimDef, questionIdToPath);
};

export const scoreRespondentForDimPercent = (respondent, dimDef, deps) => {
  if (!dimDef) return null;
  const raw = scoreRespondentForDim(respondent, dimDef, deps);
  if (raw == null) return null;
  const min = Number(dimDef?.scale?.min ?? 0);
  const max = Number(dimDef?.scale?.max ?? 10);
  return normalizeToPercent(raw, min, max);
};

export const scoreAnchorOptionForDimPercent = (respondents, dimDef, minResponses, deps) => {
  if (!respondents || respondents.length <= minResponses) return null;
  if (!dimDef) return null;

  const perRespondent = respondents
    .map((r) => scoreRespondentForDim(r, dimDef, deps))
    .filter((v) => v != null);

  const rawAvg = average(perRespondent);
  if (rawAvg == null) return null;

  const min = Number(dimDef?.scale?.min ?? 0);
  const max = Number(dimDef?.scale?.max ?? 10);
  return normalizeToPercent(rawAvg, min, max);
};

export const computeQuadrantInclusionForRespondents = (respondents, dim1Def, dim2Def, quadrantConfig, deps) => {
  const points = (respondents || [])
    .map((r) => ({
      x: scoreRespondentForDimPercent(r, dim1Def, deps),
      y: scoreRespondentForDimPercent(r, dim2Def, deps),
    }))
    .filter((p) => Number.isFinite(Number(p.x)) && Number.isFinite(Number(p.y)));

  const { counts, total: eligibleCount } = computeQuadrantCounts(points, quadrantConfig);
  const denom = (respondents || []).length || 0;
  const pct = (k) => (denom > 0 ? ((counts?.[k] || 0) / denom) * 100 : 0);

  return {
    eligibleCount,
    counts,
    percents: {
      [QUADRANT_KEYS.TL]: pct(QUADRANT_KEYS.TL),
      [QUADRANT_KEYS.TR]: pct(QUADRANT_KEYS.TR),
      [QUADRANT_KEYS.BL]: pct(QUADRANT_KEYS.BL),
      [QUADRANT_KEYS.BR]: pct(QUADRANT_KEYS.BR),
      [QUADRANT_KEYS.SUB]: pct(QUADRANT_KEYS.SUB),
    },
  };
};

